import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface TaskSubmissionPhoto {
  id: string;
  submission_id: string;
  room_name: string;
  task_text: string;
  photo_url: string;
  type: 'before' | 'after';
  display_order: number;
  created_at: string;
}

// Get all photos for a submission
export const useTaskSubmissionPhotos = (submissionId: string | undefined) => {
  return useQuery({
    queryKey: ["task-submission-photos", submissionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("task_submission_photos")
        .select("*")
        .eq("submission_id", submissionId!)
        .order("room_name", { ascending: true })
        .order("task_text", { ascending: true })
        .order("type", { ascending: true })
        .order("display_order", { ascending: true });
      
      if (error) throw error;
      return data as TaskSubmissionPhoto[];
    },
    enabled: !!submissionId,
  });
};

// Get photos for a specific task within a submission (trim + case-insensitive so before/after show on report)
export const getPhotosForTask = (
  photos: TaskSubmissionPhoto[] | undefined,
  roomName: string,
  taskText: string,
  type?: 'before' | 'after'
): TaskSubmissionPhoto[] => {
  if (!photos) return [];
  const r = roomName.trim().toLowerCase();
  const t = taskText.trim().toLowerCase();
  return photos.filter(
    (p) =>
      (p.room_name ?? '').trim().toLowerCase() === r &&
      (p.task_text ?? '').trim().toLowerCase() === t &&
      (type ? p.type === type : true)
  );
};

// Upload a single task submission photo
export const useUploadTaskSubmissionPhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      submissionId,
      roomName,
      taskText,
      file,
      type = 'after',
      displayOrder = 0,
    }: {
      submissionId: string;
      roomName: string;
      taskText: string;
      file: File;
      type?: 'before' | 'after';
      displayOrder?: number;
    }) => {
      const roomSlug = roomName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "");
      const taskSlug = taskText.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "").slice(0, 30);
      const timestamp = Date.now();
      const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const filePath = `${submissionId}/${roomSlug}/${taskSlug}-${type}-${timestamp}.${fileExt}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from("cleaning-photos")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from("cleaning-photos")
        .getPublicUrl(filePath);

      // Insert photo record
      const { data, error: dbError } = await supabase
        .from("task_submission_photos")
        .insert({
          submission_id: submissionId,
          room_name: roomName,
          task_text: taskText,
          photo_url: urlData.publicUrl,
          type,
          display_order: displayOrder,
        })
        .select()
        .single();

      if (dbError) throw dbError;

      return data as TaskSubmissionPhoto;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["task-submission-photos", variables.submissionId],
      });
    },
  });
};

// Upload multiple photos for a task
export const useUploadMultipleTaskSubmissionPhotos = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      submissionId,
      roomName,
      taskText,
      files,
      type = 'after',
    }: {
      submissionId: string;
      roomName: string;
      taskText: string;
      files: File[];
      type?: 'before' | 'after';
    }) => {
      const roomSlug = roomName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "");
      const taskSlug = taskText.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "").slice(0, 30);
      
      const uploadedPhotos: TaskSubmissionPhoto[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const timestamp = Date.now() + i;
        const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
        const filePath = `${submissionId}/${roomSlug}/${taskSlug}-${type}-${timestamp}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("cleaning-photos")
          .upload(filePath, file, { upsert: true });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from("cleaning-photos")
          .getPublicUrl(filePath);

        const { data, error: dbError } = await supabase
          .from("task_submission_photos")
          .insert({
            submission_id: submissionId,
            room_name: roomName,
            task_text: taskText,
            photo_url: urlData.publicUrl,
            type,
            display_order: i,
          })
          .select()
          .single();

        if (dbError) throw dbError;

        uploadedPhotos.push(data as TaskSubmissionPhoto);
      }

      return uploadedPhotos;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["task-submission-photos", variables.submissionId],
      });
    },
  });
};

// Delete a single photo
export const useDeleteTaskSubmissionPhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      submissionId,
      photoUrl,
    }: {
      id: string;
      submissionId: string;
      photoUrl: string;
    }) => {
      const { error: dbError } = await supabase
        .from("task_submission_photos")
        .delete()
        .eq("id", id);

      if (dbError) throw dbError;

      // Try to delete from storage
      try {
        const url = new URL(photoUrl);
        const pathParts = url.pathname.split("/cleaning-photos/");
        if (pathParts[1]) {
          await supabase.storage.from("cleaning-photos").remove([pathParts[1]]);
        }
      } catch (e) {
        console.warn("Could not delete file from storage:", e);
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["task-submission-photos", variables.submissionId],
      });
    },
  });
};

// Delete all photos for a specific task
export const useDeleteTaskSubmissionPhotosForTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      submissionId,
      roomName,
      taskText,
      type,
      photos,
    }: {
      submissionId: string;
      roomName: string;
      taskText: string;
      type?: 'before' | 'after';
      photos: TaskSubmissionPhoto[];
    }) => {
      let query = supabase
        .from("task_submission_photos")
        .delete()
        .eq("submission_id", submissionId)
        .eq("room_name", roomName)
        .eq("task_text", taskText);
      
      if (type) {
        query = query.eq("type", type);
      }

      const { error: dbError } = await query;

      if (dbError) throw dbError;

      // Try to delete from storage
      for (const photo of photos) {
        if (type && photo.type !== type) continue;
        try {
          const url = new URL(photo.photo_url);
          const pathParts = url.pathname.split("/cleaning-photos/");
          if (pathParts[1]) {
            await supabase.storage.from("cleaning-photos").remove([pathParts[1]]);
          }
        } catch (e) {
          console.warn("Could not delete file from storage:", e);
        }
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["task-submission-photos", variables.submissionId],
      });
    },
  });
};
